import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import {
  CreditCard, CheckCircle, Clock, XCircle, AlertCircle, Shield, RefreshCw, Search
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const statusBadge = {
  pending: { bg: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  active: { bg: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
  rejected: { bg: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
};

const cardTypes = [
  'Visa/MC', 'PIN Debit', 'Gift Card', 'American Express', 'EBT',
  'Discover/JCB', 'ACH',
];

const emptyVarSheet = {
  dba: '', street_address: '', city: '', state: '', zip_code: '', phone: '',
  merchant_number: '', v_number: '', mcc: '', bin_number: '', chain: '',
  agent_bank: '', api_keys: '', token: '', store_number: '', terminal_number: '',
  location_number: '', card_types_accepted: [], approved_monthly_volume: '',
};

export default function MerchantApprovals() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [filtered, setFiltered] = useState([]);

  // VAR Sheet dialog
  const [varDialogOpen, setVarDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [varSheet, setVarSheet] = useState(emptyVarSheet);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadAccounts(); }, []);

  useEffect(() => {
    let list = accounts;
    if (statusFilter !== 'all') list = list.filter((a) => a.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((a) =>
        a.user_name?.toLowerCase().includes(q) ||
        a.user_email?.toLowerCase().includes(q) ||
        a.tenant_name?.toLowerCase().includes(q)
      );
    }
    setFiltered(list);
  }, [accounts, statusFilter, search]);

  const loadAccounts = async () => {
    try {
      const data = await api.getMerchantAccounts();
      setAccounts(data);
    } catch (err) {
      toast.error('Failed to load merchant accounts');
    } finally {
      setLoading(false);
    }
  };

  const openVarSheet = (account) => {
    setSelectedAccount(account);
    setVarSheet(account.var_sheet ? { ...emptyVarSheet, ...account.var_sheet } : { ...emptyVarSheet });
    setVarDialogOpen(true);
  };

  const handleActivate = async () => {
    if (!varSheet.merchant_number) { toast.error('Merchant number is required'); return; }
    setSaving(true);
    try {
      await api.activateMerchantAccount(selectedAccount.id, varSheet);
      toast.success('Merchant account activated!');
      setVarDialogOpen(false);
      loadAccounts();
    } catch (err) {
      toast.error(err.message || 'Activation failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async (accountId) => {
    try {
      await api.rejectMerchantAccount(accountId);
      toast.success('Application rejected');
      loadAccounts();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  };

  const setVarField = (k, v) => setVarSheet((s) => ({ ...s, [k]: v }));

  const toggleCardType = (type) => {
    setVarSheet((s) => ({
      ...s,
      card_types_accepted: s.card_types_accepted.includes(type)
        ? s.card_types_accepted.filter((t) => t !== type)
        : [...s.card_types_accepted, type],
    }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="merchant-approvals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Merchant Account Approvals</h1>
          <p className="text-sm text-muted-foreground mt-1">{accounts.length} applications</p>
        </div>
        <Button variant="outline" size="icon" onClick={loadAccounts}><RefreshCw className="h-4 w-4" /></Button>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search by user or tenant..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="search-merchant-apps" />
          </div>
          {['all', 'pending', 'active', 'rejected'].map((s) => (
            <Button
              key={s}
              variant={statusFilter === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(s)}
              className={statusFilter === s ? 'bg-[#3b82f6] hover:bg-[#2563eb] text-white' : ''}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </Button>
          ))}
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="merchant-accounts-table">
            <thead>
              <tr className="border-b text-left text-sm text-muted-foreground">
                <th className="px-6 py-3 font-medium">Applicant</th>
                <th className="px-6 py-3 font-medium">Tenant</th>
                <th className="px-6 py-3 font-medium">Role</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Applied</th>
                <th className="px-6 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">No merchant applications</td></tr>}
              {filtered.map((acc) => {
                const badge = statusBadge[acc.status] || statusBadge.pending;
                const Icon = badge.icon;
                return (
                  <tr key={acc.id} className="border-b last:border-0 hover:bg-accent/50 transition-colors" data-testid={`merchant-row-${acc.id}`}>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-sm">{acc.user_name || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground">{acc.user_email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">{acc.tenant_name || '-'}</td>
                    <td className="px-6 py-4">
                      <span className="text-xs font-medium px-2 py-1 bg-accent rounded capitalize">{acc.user_role?.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${badge.bg}`}>
                        <Icon className="h-3 w-3" /> {acc.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {acc.applied_at ? new Date(acc.applied_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {acc.status === 'pending' && (
                          <>
                            <Button size="sm" className="bg-[#3b82f6] hover:bg-[#2563eb] text-white" onClick={() => openVarSheet(acc)} data-testid={`activate-merchant-${acc.id}`}>
                              <Shield className="h-3.5 w-3.5 mr-1" /> Activate
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleReject(acc.id)} data-testid={`reject-merchant-${acc.id}`}>
                              Reject
                            </Button>
                          </>
                        )}
                        {acc.status === 'active' && (
                          <Button size="sm" variant="outline" onClick={() => openVarSheet(acc)} data-testid={`edit-var-sheet-${acc.id}`}>
                            Edit VAR Sheet
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* VAR Sheet Dialog */}
      <Dialog open={varDialogOpen} onOpenChange={setVarDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedAccount?.status === 'active' ? 'Edit VAR Sheet' : 'Activate Merchant Account'}
              {selectedAccount?.user_name ? ` — ${selectedAccount.user_name}` : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* Business Info */}
            <div>
              <p className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Business Information</p>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>DBA *</Label><Input value={varSheet.dba} onChange={(e) => setVarField('dba', e.target.value)} className="mt-1" data-testid="var-dba" /></div>
                <div><Label>Phone</Label><Input value={varSheet.phone} onChange={(e) => setVarField('phone', e.target.value)} className="mt-1" /></div>
                <div className="col-span-2"><Label>Street Address</Label><Input value={varSheet.street_address} onChange={(e) => setVarField('street_address', e.target.value)} className="mt-1" /></div>
                <div><Label>City</Label><Input value={varSheet.city} onChange={(e) => setVarField('city', e.target.value)} className="mt-1" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>State</Label><Input value={varSheet.state} onChange={(e) => setVarField('state', e.target.value)} className="mt-1" /></div>
                  <div><Label>ZIP</Label><Input value={varSheet.zip_code} onChange={(e) => setVarField('zip_code', e.target.value)} className="mt-1" /></div>
                </div>
              </div>
            </div>

            {/* Processor Parameters */}
            <div>
              <p className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Processor Parameters</p>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Merchant Number *</Label><Input value={varSheet.merchant_number} onChange={(e) => setVarField('merchant_number', e.target.value)} className="mt-1 font-mono" data-testid="var-merchant-number" /></div>
                <div><Label>V Number</Label><Input value={varSheet.v_number} onChange={(e) => setVarField('v_number', e.target.value)} className="mt-1 font-mono" /></div>
                <div><Label>MCC</Label><Input value={varSheet.mcc} onChange={(e) => setVarField('mcc', e.target.value)} className="mt-1 font-mono" /></div>
                <div><Label>BIN</Label><Input value={varSheet.bin_number} onChange={(e) => setVarField('bin_number', e.target.value)} className="mt-1 font-mono" /></div>
                <div><Label>Chain</Label><Input value={varSheet.chain} onChange={(e) => setVarField('chain', e.target.value)} className="mt-1 font-mono" /></div>
                <div><Label>Agent Bank</Label><Input value={varSheet.agent_bank} onChange={(e) => setVarField('agent_bank', e.target.value)} className="mt-1 font-mono" /></div>
              </div>
            </div>

            {/* Terminal Info */}
            <div>
              <p className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Terminal Configuration</p>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Store Number</Label><Input value={varSheet.store_number} onChange={(e) => setVarField('store_number', e.target.value)} className="mt-1 font-mono" /></div>
                <div><Label>Terminal Number</Label><Input value={varSheet.terminal_number} onChange={(e) => setVarField('terminal_number', e.target.value)} className="mt-1 font-mono" /></div>
                <div><Label>Location Number</Label><Input value={varSheet.location_number} onChange={(e) => setVarField('location_number', e.target.value)} className="mt-1 font-mono" /></div>
              </div>
            </div>

            {/* API Credentials */}
            <div>
              <p className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">API Credentials</p>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>API Keys</Label><Input value={varSheet.api_keys} onChange={(e) => setVarField('api_keys', e.target.value)} className="mt-1 font-mono" data-testid="var-api-keys" /></div>
                <div><Label>Token</Label><Input value={varSheet.token} onChange={(e) => setVarField('token', e.target.value)} className="mt-1 font-mono" data-testid="var-token" /></div>
              </div>
            </div>

            {/* Card Types */}
            <div>
              <p className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Card Types Accepted</p>
              <div className="flex flex-wrap gap-3">
                {cardTypes.map((type) => (
                  <label key={type} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={varSheet.card_types_accepted.includes(type)}
                      onCheckedChange={() => toggleCardType(type)}
                    />
                    <span className="text-sm">{type}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Volume */}
            <div>
              <Label>Approved Monthly Volume ($)</Label>
              <Input value={varSheet.approved_monthly_volume} onChange={(e) => setVarField('approved_monthly_volume', e.target.value)} className="mt-1 max-w-xs font-mono" placeholder="e.g. 50000" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVarDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#3b82f6] hover:bg-[#2563eb] text-white" onClick={handleActivate} disabled={saving} data-testid="save-var-sheet-btn">
              {saving ? 'Saving...' : selectedAccount?.status === 'active' ? 'Update VAR Sheet' : 'Activate Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
